// LiveKit room — auto-connects, viewer/creator/cohost role based, falls back to MP4 silently.
// Adds a viewer-side "Sound ON/OFF" toggle so browser autoplay policy no longer
// silences remote audio (OBS + mobile broadcasters were sending audio, but the
// <audio autoplay> element was blocked until the user interacted). Default is
// muted so the video still autoplays; one tap flips both mute + explicit play().
import { useEffect, useRef, useState } from "react";
import { LiveKitRoom, GridLayout, ParticipantTile, ControlBar, RoomAudioRenderer, useTracks, useRemoteParticipants } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Volume2, VolumeX } from "lucide-react";
import "@livekit/components-styles";
import api from "../lib/api";

function StageTiles() {
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true },
                            { source: Track.Source.ScreenShare, withPlaceholder: false }]);
  // Show only participants who can publish — the streamer plus any accepted
  // cohosts. Everyone else (viewers on both sides) is hidden from the grid.
  const visibleTracks = tracks.filter(
    (tr) => tr.participant?.permissions?.canPublish
  );
  return (
    <GridLayout tracks={visibleTracks} style={{ height: "100%" }}>
      <ParticipantTile />
    </GridLayout>
  );
}

// Viewer sound toggle — sits over the video. Default OFF so the video autoplays
// (browsers only allow silent autoplay). One tap unmutes AND explicitly calls
// .play() on every RoomAudioRenderer <audio> element so autoplay-blocked ones
// start immediately.
function SoundToggle({ soundOn, setSoundOn, rootRef }) {
  const handleClick = () => {
    const next = !soundOn;
    setSoundOn(next);
    if (next && rootRef.current) {
      // Explicitly kick every audio element out of the browser's autoplay block.
      rootRef.current.querySelectorAll("audio").forEach((a) => {
        try { a.muted = false; a.volume = 1; const p = a.play?.(); if (p && typeof p.catch === "function") p.catch(() => {}); } catch {}
      });
    }
  };
  return (
    <button
      onClick={handleClick}
      data-testid="stream-sound-toggle"
      title={soundOn ? "Mute stream" : "Turn sound ON"}
      className={`absolute top-3 right-3 z-40 flex items-center gap-1.5 rounded-full backdrop-blur px-3 py-1.5 text-white text-[11px] font-display tracking-widest uppercase shadow-lg transition-colors ${soundOn ? "bg-black/60 hover:bg-black/80" : "bg-nexus-cyan text-black hover:bg-nexus-cyan/90 animate-pulse"}`}
    >
      {soundOn ? <><Volume2 className="w-3.5 h-3.5" /> Sound</> : <><VolumeX className="w-3.5 h-3.5" /> Tap for sound</>}
    </button>
  );
}

// StreamEndedWatcher — viewer-only. Watches the LiveKit room's remote
// participant list; when every publisher (streamer + any cohost) has
// disconnected for more than GRACE ms, treats the stream as ended and:
//   1. Renders an on-tile "Stream has ended" placeholder
//   2. Calls onStreamEnded?.() so the parent page (Watch.jsx / Studio.jsx)
//      can react (e.g. redirect, refetch is_live status).
// Mirrors the RemoteJoin/RemoteLeft watcher pattern used in Messages.jsx.
// Purely client-side reaction to LiveKit's own ParticipantDisconnected event;
// no backend call. Does nothing for creators/cohosts (they ARE the publisher).
function StreamEndedWatcher({ enabled, onEndedChange, onStreamEnded }) {
  const remotes = useRemoteParticipants();
  const hadPublisherRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    const publishers = remotes.filter((p) => p?.permissions?.canPublish);
    if (publishers.length > 0) {
      hadPublisherRef.current = true;
      onEndedChange?.(false);
      return;
    }
    // No publishers present. If one WAS present at some point in this
    // session, wait a short grace window (network blip / camera-swap on
    // mobile is fine) then flip to "ended".
    if (!hadPublisherRef.current) return;
    const t = setTimeout(() => {
      onEndedChange?.(true);
      onStreamEnded?.();
    }, 2500);
    return () => clearTimeout(t);
  }, [enabled, remotes, onEndedChange, onStreamEnded]);
  return null;
}

export default function LiveKitStreamRoom({ streamId, role = "viewer", roomOverride, onError, onStreamEnded, fallback }) {
  const [tok, setTok] = useState(null);
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [soundOn, setSoundOn] = useState(false); // default MUTED to satisfy browser autoplay policy
  const [ended, setEnded] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    const payload = { stream_id: streamId, role };
    if (roomOverride) payload.room_override = roomOverride;
    api.post("/livekit/token", payload)
      .then(({ data }) => { if (!cancel) { setTok(data.token); setUrl(data.url); } })
      .catch((e) => {
        if (cancel) return;
        const msg = e?.response?.data?.detail || "LiveKit unavailable";
        setErr(msg); onError?.(msg);
      });
    return () => { cancel = true; };
  }, [streamId, role]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return fallback || (<div className="aspect-video bg-nexus-surface border border-white/10 flex items-center justify-center text-xs text-white/40">LiveKit: {err}</div>);
  if (!tok || !url) return <div className="aspect-video bg-nexus-surface border border-white/10 flex items-center justify-center text-xs text-white/40">Connecting live…</div>;

  const isViewer = role === "viewer";
  // "self_view" is the creator watching their OWN broadcast back on the Studio
  // Dashboard -- same room, same subscribe-only behaviour as a viewer (so it
  // shows whatever OBS/mobile is actually sending). Distinct from "creator",
  // which DOES publish local mic/camera (used for the actual in-browser
  // broadcast flow).
  const isPublisher = role !== "viewer" && role !== "self_view";
  // self_view needs the same sound-unmute control as a viewer -- it's also
  // subscribe-only audio, blocked by the same autoplay policy. Without this,
  // self_view had NO way to ever hear audio: RoomAudioRenderer below starts
  // muted for every role and only the SoundToggle button can flip it on, but
  // that button used to be gated to isViewer only, permanently silencing the
  // Studio Dashboard's own live preview (this is what showed up as "OBS audio
  // not coming through" -- video played fine since muted video autoplay is
  // allowed, audio just had no escape hatch).
  const canControlSound = isViewer || role === "self_view";

  return (
    <div ref={rootRef} className="w-full h-full relative" data-testid="livekit-room" data-lk-theme="default">
      <LiveKitRoom token={tok} serverUrl={url} connect audio={isPublisher} video={isPublisher} style={{ height: "100%" }}>
        <div className="flex flex-col h-full">
          <div className="flex-1 min-h-0 relative">
            <StageTiles />
            {/* Viewer sound control (creators / cohosts have LiveKit's built-in
                ControlBar mic button below, so they don't need this) */}
            {canControlSound && <SoundToggle soundOn={soundOn} setSoundOn={setSoundOn} rootRef={rootRef} />}
            {/* Detect the streamer (and any cohosts) disconnecting so viewers
                aren't stuck staring at a frozen last frame when OBS / mobile
                broadcast stops. Only mounted for viewers — creators/cohosts
                are publishers themselves. */}
            {isViewer && <StreamEndedWatcher enabled={true} onEndedChange={setEnded} onStreamEnded={onStreamEnded} />}
            {isViewer && ended && (
              <div className="absolute inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center text-white gap-2" data-testid="stream-ended-overlay">
                <div className="text-xs font-display tracking-[0.35em] uppercase text-white/60">// broadcast</div>
                <div className="text-lg font-display font-bold">Stream has ended</div>
                <div className="text-xs text-white/50">The creator has stopped broadcasting.</div>
              </div>
            )}
          </div>
          {isPublisher && (
            <ControlBar controls={{ microphone: true, camera: true, screenShare: true, leave: false }} variation="minimal" />
          )}
          {/* RoomAudioRenderer renders <audio> for every remote audio track.
              volume={0} + muted={true} keeps the browser happy while video
              autoplays. Flips to volume=1 the moment the viewer taps the button. */}
          <RoomAudioRenderer volume={soundOn ? 1 : 0} muted={!soundOn} />
        </div>
      </LiveKitRoom>
    </div>
  );
}
